import React, { useState, useEffect, useContext, useRef } from 'react';
import { useLocation, useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { AuthContext } from '../App';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { MessageSquare, Plus, Send, ArrowDown } from 'lucide-react';
import { toast } from 'sonner';
import BackToResultsButton from '../components/BackToResultsButton';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const MessagesPage = () => {
  const { user } = useContext(AuthContext);
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [conversations, setConversations] = useState([]);
  const [selectedThread, setSelectedThread] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNewDialog, setShowNewDialog] = useState(false);
  const [sendingMessage, setSendingMessage] = useState(false);
  const [hasProcessedLocationState, setHasProcessedLocationState] = useState(false);
  
  // Ref for auto-scrolling messages
  const messagesEndRef = useRef(null);
  const messagesContainerRef = useRef(null);
  
  const [newMessage, setNewMessage] = useState({
    to_company_id: '',
    subject: '',
    content: '',
    resource_id: null
  });

  const [replyContent, setReplyContent] = useState('');

  // Helper to strip HTML tags for preview
  const stripHtml = (html) => {
    if (!html) return '';
    // Remove HTML tags and decode entities
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    return tmp.textContent || tmp.innerText || '';
  };
  
  // Auto-scroll to bottom when messages change or thread is selected
  const scrollToBottom = () => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };
  
  // Scroll to bottom when messages are loaded or updated
  useEffect(() => {
    if (messages.length > 0) {
      // Small delay to ensure DOM is updated
      setTimeout(scrollToBottom, 100);
    }
  }, [messages, selectedThread]);

  useEffect(() => {
    fetchConversations();
  }, []);

  // Open specific thread from URL query parameter (from Reservations page)
  useEffect(() => {
    const threadId = searchParams.get('thread');
    if (threadId && conversations.length > 0) {
      const conversation = conversations.find(c => c.thread_id === threadId);
      if (conversation) {
        fetchThread(threadId);
      }
    }
  }, [searchParams, conversations]);

  // Handle navigation from notifications with thread parameter
  useEffect(() => {
    const threadId = searchParams.get('thread');
    
    if (threadId && conversations.length > 0 && !hasProcessedLocationState) {
      // Mark as processed to prevent re-triggering
      setHasProcessedLocationState(true);
      
      // Open the conversation
      setSelectedThread(threadId);
      
      // Fetch thread messages
      const token = localStorage.getItem('token');
      axios.get(`${API}/messages/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      }).then(res => {
        setMessages(res.data);
        toast.success('Opening conversation');
      }).catch(err => {
        console.error('Failed to load thread:', err);
      });
      
      // Remove query params after opening conversation
      setTimeout(() => navigate('/messages', { replace: true }), 500);
    }
  }, [searchParams, conversations, hasProcessedLocationState, navigate]);

  // Handle navigation from Company Profile page with URL params
  useEffect(() => {
    const companyId = searchParams.get('company');
    const companyName = searchParams.get('companyName');
    
    if (companyId && companyName && conversations.length > 0 && !hasProcessedLocationState) {
      // Mark as processed to prevent re-triggering
      setHasProcessedLocationState(true);
      
      // Find existing conversation with this company
      const existingConversation = conversations.find(
        conv => conv.other_company_id === companyId
      );
      
      if (existingConversation) {
        // Open existing conversation
        fetchThread(existingConversation.thread_id);
        toast.success(`Opening conversation with ${companyName}`);
        // Remove query params after opening conversation
        setTimeout(() => navigate('/messages', { replace: true }), 500);
      } else {
        // No existing conversation - open new message dialog
        setNewMessage({
          to_company_id: companyId,
          subject: '',
          content: ''
        });
        setShowNewDialog(true);
        toast.info(`Starting new conversation with ${companyName}`);
        // Remove query params after dialog opens
        setTimeout(() => navigate('/messages', { replace: true }), 500);
      }
    }
  }, [searchParams, conversations, hasProcessedLocationState, navigate]);

  // Handle navigation from Favorites/Matching page with resource or project info
  useEffect(() => {
    if (location.state?.targetCompanyId && conversations.length > 0 && !hasProcessedLocationState) {
      const { targetCompanyId, targetCompanyName, resourceInfo, projectInfo, prefilledMessage } = location.state;
      
      // Mark as processed to prevent re-triggering
      setHasProcessedLocationState(true);
      
      // Determine the context: project or resource
      const isProjectContext = projectInfo && !resourceInfo;
      const isResourceContext = resourceInfo && !projectInfo;
      
      // Find existing conversation
      let existingConversation;
      if (isResourceContext) {
        // PM contacting about a specific resource
        existingConversation = conversations.find(
          conv => conv.other_company_id === targetCompanyId && 
                  conv.resource_id === resourceInfo?.id
        );
      } else if (isProjectContext) {
        // RM contacting about a specific project
        existingConversation = conversations.find(
          conv => conv.other_company_id === targetCompanyId && 
                  conv.project_id === projectInfo?.id
        );
      } else {
        // Both resource and project info provided
        existingConversation = conversations.find(
          conv => conv.other_company_id === targetCompanyId
        );
      }
      
      if (existingConversation) {
        // Open existing conversation
        fetchThread(existingConversation.thread_id);
        const contextName = isProjectContext ? projectInfo?.title : (resourceInfo?.name || 'conversation');
        toast.success(`Opening conversation about ${contextName}`);
      } else {
        // Create new conversation with appropriate context
        let subject, content;
        
        if (isProjectContext) {
          // RM contacting PM about a project
          subject = `Inquiry about project: ${projectInfo?.title}`;
          content = prefilledMessage || `Hello,\n\nI am interested in the project "${projectInfo?.title}".\n\nBest regards`;
        } else if (isResourceContext) {
          // PM contacting RM about a resource
          subject = `Inquiry about ${resourceInfo?.name || 'consultant'}`;
          content = prefilledMessage || `Hello,\n\nI am interested in learning more about ${resourceInfo?.name} (${resourceInfo?.role}).\n\nBest regards`;
        } else {
          // Both contexts provided (from matching page)
          subject = `Inquiry about project: ${projectInfo?.title}`;
          content = prefilledMessage || `Hello,\n\nRegarding the project "${projectInfo?.title}".\n\nBest regards`;
        }
        
        setNewMessage({
          to_company_id: targetCompanyId,
          subject: subject,
          content: content,
          resource_id: resourceInfo?.id || null,
          project_id: projectInfo?.id || null
        });
        setShowNewDialog(true);
        const contextName = isProjectContext ? `project: ${projectInfo?.title}` : (resourceInfo?.name || 'conversation');
        toast.info(`Starting new conversation about ${contextName}`);
      }
      
      // Clear the state to prevent re-triggering
      window.history.replaceState({}, document.title);
    }
  }, [conversations, location.state, hasProcessedLocationState]);

  const fetchConversations = async () => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/messages/conversations`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setConversations(response.data);
    } catch (error) {
      toast.error('Failed to load conversations');
    } finally {
      setLoading(false);
    }
  };

  const fetchThread = async (threadId) => {
    const token = localStorage.getItem('token');
    try {
      const response = await axios.get(`${API}/messages/thread/${threadId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setMessages(response.data);
      setSelectedThread(threadId);
      
      // Update the conversations list to reflect read status
      // The backend marks messages as read when fetching a thread
      setConversations(prev => prev.map(conv => 
        conv.thread_id === threadId 
          ? { ...conv, unread_count: 0 } 
          : conv
      ));
      
      // Dispatch an event to update the sidebar badge
      window.dispatchEvent(new CustomEvent('messagesRead'));
    } catch (error) {
      toast.error('Failed to load messages');
    }
  };

  const sendNewMessage = async (e) => {
    e.preventDefault();
    
    if (sendingMessage) return; // Prevent double submission
    
    const token = localStorage.getItem('token');
    setSendingMessage(true);
    
    try {
      const response = await axios.post(`${API}/messages`, newMessage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Message sent successfully');
      
      // Close dialog and reset form
      setShowNewDialog(false);
      setNewMessage({ to_company_id: '', subject: '', content: '', resource_id: null });
      
      // Refresh conversations
      await fetchConversations();
      
      // Open the newly created conversation if we have the thread_id from response
      if (response.data?.thread_id) {
        setTimeout(() => {
          fetchThread(response.data.thread_id);
        }, 300); // Small delay to allow conversations list to update
      }
    } catch (error) {
      console.error('Send message error:', error.response?.data || error);
      // Handle error detail - could be string or validation error object
      let errorMessage = 'Failed to send message';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors
          errorMessage = detail.map(e => e.msg).join(', ');
        }
      }
      toast.error(errorMessage);
    } finally {
      setSendingMessage(false);
    }
  };

  const sendReply = async () => {
    if (!replyContent.trim()) return;

    const token = localStorage.getItem('token');
    const conversation = conversations.find(c => c.thread_id === selectedThread);
    
    if (!conversation) {
      toast.error('Conversation not found');
      return;
    }
    
    // Validate required fields
    if (!conversation.other_company_id) {
      console.error('Missing other_company_id:', conversation);
      toast.error('Cannot send reply: recipient company not found');
      return;
    }
    
    if (!conversation.latest_message?.subject) {
      console.error('Missing subject:', conversation);
      toast.error('Cannot send reply: conversation subject not found');
      return;
    }
    
    try {
      // Keep original subject, don't add "Re:" repeatedly
      let subject = conversation.latest_message.subject;
      if (!subject.startsWith('Re: ')) {
        subject = `Re: ${subject}`;
      }
      
      const payload = {
        to_company_id: conversation.other_company_id,
        subject: subject,
        content: replyContent,
        thread_id: selectedThread
      };
      
      console.log('Sending reply with payload:', payload);
      
      await axios.post(`${API}/messages`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Reply sent');
      setReplyContent('');
      fetchThread(selectedThread);
      fetchConversations();
    } catch (error) {
      console.error('Reply error:', error.response?.data || error);
      // Handle error detail - could be string or validation error object
      let errorMessage = 'Failed to send reply';
      if (error.response?.data?.detail) {
        const detail = error.response.data.detail;
        if (typeof detail === 'string') {
          errorMessage = detail;
        } else if (Array.isArray(detail)) {
          // Pydantic validation errors - show which field is missing
          errorMessage = detail.map(e => `${e.loc.join('.')}: ${e.msg}`).join(', ');
        }
      }
      toast.error(errorMessage);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50" data-testid="messages-page">

      <main className="max-w-7xl mx-auto px-6 py-8 ">
        {/* Back to Results Button */}
        <div className="mb-4">
          <BackToResultsButton />
        </div>
        
        <div className="flex items-center justify-between mb-8 fade-in">
          <div>
            <h1 className="text-4xl font-bold mb-2" style={{ color: '#1B3240' }}>Messages</h1>
            <p className="text-lg text-gray-600">Company-to-company conversations</p>
          </div>
          <Dialog open={showNewDialog} onOpenChange={setShowNewDialog}>
            <DialogTrigger asChild>
              <Button 
                className="btn-snow"
                data-testid="new-message-btn"
              >
                <Plus className="w-4 h-4 mr-2" />
                New Message
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle>New Message</DialogTitle>
              </DialogHeader>
              <form onSubmit={sendNewMessage} className="space-y-4">
                <div>
                  <Label htmlFor="to_company_id">To Company ID</Label>
                  <Input
                    id="to_company_id"
                    value={newMessage.to_company_id}
                    onChange={(e) => setNewMessage({ ...newMessage, to_company_id: e.target.value })}
                    required
                    data-testid="message-company-input"
                  />
                </div>
                <div>
                  <Label htmlFor="subject">Subject</Label>
                  <Input
                    id="subject"
                    value={newMessage.subject}
                    onChange={(e) => setNewMessage({ ...newMessage, subject: e.target.value })}
                    required
                    data-testid="message-subject-input"
                  />
                </div>
                <div>
                  <Label htmlFor="content">Message</Label>
                  <Textarea
                    id="content"
                    value={newMessage.content}
                    onChange={(e) => setNewMessage({ ...newMessage, content: e.target.value })}
                    rows={4}
                    required
                    data-testid="message-content-input"
                  />
                </div>
                <Button 
                  type="submit" 
                  className="w-full btn-snow"
                  data-testid="send-message-btn"
                  disabled={sendingMessage}
                >
                  <Send className="w-4 h-4 mr-2" />
                  {sendingMessage ? 'Sending...' : 'Send Message'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="grid md:grid-cols-3 gap-6">
          <div className="md:col-span-1">
            <Card>
              <CardHeader>
                <CardTitle>Conversations</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                {loading ? (
                  <div className="p-6 text-center">
                    <div className="spinner mx-auto"></div>
                  </div>
                ) : conversations.length === 0 ? (
                  <div className="p-6 text-center text-gray-600">
                    <MessageSquare className="w-12 h-12 text-emerald-300 mx-auto mb-2" />
                    <p>No conversations yet</p>
                  </div>
                ) : (
                  <div className="divide-y divide-gray-100">
                    {conversations.map((conv) => (
                      <div
                        key={conv.thread_id}
                        className={`p-4 cursor-pointer transition-colors ${
                          selectedThread === conv.thread_id 
                            ? 'bg-emerald-50 border-l-4 border-emerald-500' 
                            : 'hover:bg-gray-50'
                        }`}
                        onClick={() => fetchThread(conv.thread_id)}
                        data-testid={`conversation-${conv.thread_id}`}
                      >
                        <div className="flex items-start justify-between mb-1">
                          <div className="flex-1">
                            <div className="flex items-center space-x-2">
                              <h3 className="font-semibold text-gray-900">
                                {/* Display "Snow Exchange Operating Partner" for the SCS company */}
                                {conv.other_company?.id === '81d4c9e3-139d-4ea0-ba86-e106396d69a8' 
                                  ? 'Snow Exchange Operating Partner'
                                  : (conv.other_company?.name || 'Unknown Company')
                                }
                              </h3>
                              {conv.reservation && (
                                <span className="px-2 py-0.5 text-xs rounded-full bg-gradient-to-r from-emerald-500 to-teal-500 text-white">
                                  📅 Reservation
                                </span>
                              )}
                            </div>
                            <p className="text-xs text-gray-500 mt-0.5">
                              {conv.latest_message.subject}
                            </p>
                          </div>
                          {conv.unread_count > 0 && (
                            <span className="px-2 py-1 text-white text-xs rounded-full bg-gradient-to-r from-emerald-500 to-teal-500">
                              {conv.unread_count}
                            </span>
                          )}
                        </div>
                        <p className="text-sm text-gray-600 truncate mt-1">
                          {stripHtml(conv.latest_message.content).substring(0, 60)}
                          {stripHtml(conv.latest_message.content).length > 60 ? '...' : ''}
                        </p>
                        <p className="text-xs text-gray-400 mt-1">
                          {new Date(conv.latest_message.created_at).toLocaleString()}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>

          <div className="md:col-span-2">
            {selectedThread ? (
              <Card>
                <CardHeader className="flex flex-row items-center justify-between border-b border-gray-100">
                  <div className="flex flex-col">
                    <CardTitle className="text-gray-900">Messages</CardTitle>
                    {(() => {
                      const conv = conversations.find(c => c.thread_id === selectedThread);
                      if (!conv?.other_company) return null;
                      
                      // Display friendly name for Operating Partner
                      const displayName = conv.other_company.id === '81d4c9e3-139d-4ea0-ba86-e106396d69a8'
                        ? 'Snow Exchange Operating Partner'
                        : conv.other_company.name;
                      
                      return (
                        <button
                          onClick={() => navigate(`/company/${conv.other_company.id}`)}
                          className="text-sm text-blue-600 hover:text-blue-800 hover:underline text-left mt-1"
                        >
                          View {displayName}&apos;s profile
                        </button>
                      );
                    })()}
                  </div>
                  {conversations.find(c => c.thread_id === selectedThread)?.reservation && (
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => {
                        const conv = conversations.find(c => c.thread_id === selectedThread);
                        const reservationId = conv?.reservation?.id || conv?.reservation?.reservation_id;
                        navigate(`/reservations?reservation=${reservationId}`);
                      }}
                      className="border-2 border-emerald-500 text-emerald-600 hover:bg-emerald-50 text-xs"
                    >
                      📅 View Reservation
                    </Button>
                  )}
                </CardHeader>
                <CardContent>
                  <div 
                    ref={messagesContainerRef}
                    className="space-y-4 mb-6 max-h-96 overflow-y-auto"
                  >
                    {messages.map((msg) => {
                      const isSystem = msg.message_type === 'system';
                      const isMine = msg.from_company_id === user.company_id;
                      
                      if (isSystem) {
                        return (
                          <div
                            key={msg.id}
                            className="p-4 rounded-lg bg-emerald-50 border-l-4 border-emerald-500"
                          >
                            <div className="flex items-center space-x-2 mb-2">
                              <span className="px-2 py-1 text-white text-xs rounded-full bg-gradient-to-r from-emerald-500 to-teal-500">
                                SYSTEM
                              </span>
                              <p className="font-semibold text-sm text-gray-700">
                                {msg.subject}
                              </p>
                            </div>
                            <div 
                              className="text-gray-900 mb-2"
                              dangerouslySetInnerHTML={{ __html: msg.content }}
                            />
                            <p className="text-xs text-gray-500">
                              {new Date(msg.created_at).toLocaleString()}
                            </p>
                          </div>
                        );
                      }
                      
                      return (
                        <div
                          key={msg.id}
                          className={`p-4 rounded-lg ${
                            isMine
                              ? 'bg-gradient-to-r from-emerald-50 to-teal-50 ml-8 border border-emerald-100'
                              : 'bg-gray-100 mr-8'
                          }`}
                        >
                          <div className="flex items-center justify-between mb-1">
                            <p className="font-semibold text-sm text-gray-700">
                              {msg.subject}
                            </p>
                            {msg.sender_name && (
                              <span className="text-xs text-gray-500 italic">
                                {msg.sender_name}
                              </span>
                            )}
                          </div>
                          {/* Check if content contains HTML tags (render as HTML, otherwise as text) */}
                          {msg.content && msg.content.includes('<') && msg.content.includes('>') ? (
                            <div 
                              className="text-gray-900 mb-2" 
                              dangerouslySetInnerHTML={{ __html: msg.content }}
                            />
                          ) : (
                            <p className="text-gray-900 mb-2 whitespace-pre-wrap">{msg.content}</p>
                          )}
                          <p className="text-xs text-gray-500">
                            {new Date(msg.created_at).toLocaleString()}
                          </p>
                        </div>
                      );
                    })}
                    {/* Invisible element to scroll to */}
                    <div ref={messagesEndRef} />
                  </div>

                  <div className="border-t pt-4">
                    <Label htmlFor="reply">Reply</Label>
                    <Textarea
                      id="reply"
                      value={replyContent}
                      onChange={(e) => setReplyContent(e.target.value)}
                      placeholder="Type your reply..."
                      rows={3}
                      className="mb-3"
                      data-testid="reply-textarea"
                    />
                    <Button 
                      onClick={sendReply}
                      className="btn-snow"
                      data-testid="send-reply-btn"
                    >
                      <Send className="w-4 h-4 mr-2" />
                      Send Reply
                    </Button>
                  </div>
                </CardContent>
              </Card>
            ) : (
              <Card className="lg:col-span-2 bg-white">
                <CardContent className="py-20 text-center">
                  <MessageSquare className="w-16 h-16 mx-auto mb-4 text-emerald-300" />
                  <p className="text-lg text-gray-600">Select a conversation to view messages</p>
                </CardContent>
              </Card>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default MessagesPage;
